import { Link } from "@tanstack/react-router";
import { Search, Menu, X, MonitorDown } from "lucide-react";
import simpletechLogoFooter from "@/assets/simpletech-logo-footer.png";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";

type Props = { active?: "home" | "about" | "services" | "reviews" | "giving" | "blog" | "contact" };

const navLinks = [
  { to: "/", label: "Home", key: "home" },
  { to: "/about", label: "About", key: "about" },
  { to: "/services", label: "Services", key: "services" },
  { to: "/reviews", label: "Reviews", key: "reviews" },
  { to: "/giving-back", label: "Giving Back", key: "giving" },
  { to: "/blog", label: "Blog", key: "blog" },
  { to: "/contact", label: "Contact", key: "contact" },
] as const;

export function SiteHeader({ active }: Props) {
  const underline = "relative pb-1 text-slate-900 after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-[3px] after:bg-sky-400";
  const idle = "hover:text-sky-500 transition-colors";
  return (
    <header className="sticky top-0 z-50 bg-white border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-20 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center shrink-0">
          <img src={simpletechLogoFooter} alt="SimpleTech logo — managed IT support and cybersecurity in Montclair and the Inland Empire" width={240} height={80} className="h-20 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-10 text-[15px] font-medium text-slate-900">
          {navLinks.map((link) => (
            <Link
              key={link.key}
              to={link.to}
              className={active === link.key ? underline : link.key === "giving" ? `${idle} text-[#1a8a44] hover:text-[#136833]` : idle}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3 sm:gap-5 shrink-1 min-w-1">
          <button aria-label="Search" className="hidden md:block text-slate-900 hover:text-sky-500 transition-colors">
            <Search className="w-5 h-5" strokeWidth={2.5} />
          </button>
          <a
            href="https://my.splashtop.com/team_deployment/download/KX5JS7AH2S43"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download Splashtop for remote support"
            className="hidden md:inline-flex items-center gap-2 border-2 border-sky-600 text-sky-700 hover:bg-sky-50 font-bold tracking-wider text-sm px-4 sm:px-5 py-2.5 rounded-md transition-colors"
          >
            <MonitorDown className="w-4 h-4" strokeWidth={2.5} aria-hidden="true" />
            REMOTE SUPPORT
          </a>
          <Link
            to="/contact"
            className="hidden md:inline-flex bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-5 sm:px-7 py-3 rounded-md transition-colors"
          >
            FREE QUOTE
          </Link>

          <Sheet>
            <SheetTrigger asChild>
              <button
                aria-label="Open menu"
                className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-slate-900 hover:bg-slate-100 transition-colors"
              >
                <Menu className="w-6 h-6" strokeWidth={2.5} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[340px] p-0">
              <div className="flex flex-col h-full">
                <div className="flex items-center px-6 h-20 border-b border-border">
                  <span className="font-bold text-lg text-slate-900">Menu</span>
                </div>
                <nav className="flex flex-col px-6 py-6 gap-1">
                  {navLinks.map((link) => (
                    <SheetClose asChild key={link.key}>
                      <Link
                        to={link.to}
                        className={`flex items-center px-4 py-3 rounded-md text-base font-medium transition-colors ${
                          active === link.key
                            ? "bg-sky-50 text-sky-600"
                            : link.key === "giving"
                            ? "text-[#1a8a44] hover:bg-green-50 hover:text-[#136833]"
                            : "text-slate-900 hover:bg-slate-100 hover:text-sky-500"
                        }`}
                      >
                        {link.label}
                      </Link>
                    </SheetClose>
                  ))}
                </nav>
                <div className="mt-auto px-6 pb-8 pt-4 border-t border-border space-y-3">
                  <a
                    href="https://my.splashtop.com/team_deployment/download/KX5JS7AH2S43"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Download Splashtop for remote support"
                    className="flex items-center justify-center gap-2 w-full border-2 border-sky-600 text-sky-700 hover:bg-sky-50 font-bold tracking-wider text-sm px-7 py-3 rounded-md transition-colors"
                  >
                    <MonitorDown className="w-4 h-4" strokeWidth={2.5} aria-hidden="true" />
                    REMOTE SUPPORT
                  </a>
                  <SheetClose asChild>
                    <Link
                      to="/contact"
                      className="flex items-center justify-center w-full bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-7 py-3 rounded-md transition-colors"
                    >
                      FREE QUOTE
                    </Link>
                  </SheetClose>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
